import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";

// Improved PWA installation hook
const useInstallPrompt = () => {
  const [installPrompt, setInstallPrompt] = useState<any>(null);
  const [isAppInstalled, setIsAppInstalled] = useState(false);
  const [installStatus, setInstallStatus] = useState<
    "available" | "unavailable" | "installed"
  >("unavailable");

  useEffect(() => {
    // Check if app is already installed
    const checkInstallState = () => {
      const isStandalone =
        window.matchMedia("(display-mode: standalone)").matches ||
        (window as any).standalone === true;

      if (isStandalone) {
        setIsAppInstalled(true);
        setInstallStatus("installed");
      }
    };

    checkInstallState();

    // Save the install prompt event for later use
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e);
      setInstallStatus("available");
      console.log("Install prompt captured");
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    // Check if app was successfully installed
    window.addEventListener("appinstalled", () => {
      setIsAppInstalled(true);
      setInstallPrompt(null);
      setInstallStatus("installed");
      console.log("App was installed");
    });

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
    };
  }, []);

  const handleInstallClick = async () => {
    if (!installPrompt) {
      console.log("No install prompt available");
      return false;
    }

    try {
      // Show the install prompt
      installPrompt.prompt();

      // Wait for the user to respond to the prompt
      const choiceResult = await installPrompt.userChoice;

      if (choiceResult.outcome === "accepted") {
        console.log("User accepted the install prompt");
      } else {
        console.log("User dismissed the install prompt");
      }

      // We no longer need the prompt
      setInstallPrompt(null);
      setInstallStatus("unavailable");
      return choiceResult.outcome === "accepted";
    } catch (error) {
      console.error("Install prompt error:", error);
      return false;
    }
  };

  return { installPrompt, isAppInstalled, installStatus, handleInstallClick };
};

const Dashboard = () => {
  const { installPrompt, isAppInstalled, installStatus, handleInstallClick } =
    useInstallPrompt();
  const [showInstallInstructions, setShowInstallInstructions] = useState(false);
  const { currentUser, isApproved, isAdmin, isProfileComplete } = useAuth();

  // Device detection
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);

  const handleInstallButtonClick = async () => {
    // If the prompt is available, try to use it
    if (installStatus === "available") {
      const success = await handleInstallClick();
      if (!success) {
        setShowInstallInstructions(true);
      }
    } else {
      // Otherwise show manual instructions
      setShowInstallInstructions(true);
    }
  };

  return (
    <div className="container mx-auto px-4">
      <p className="text-3xl font-bold mb-2">Dashboard</p>
      <p className="text-gold font-josefin text-xl">
        Welcome{" "}
        <span className="font-bold">
          {currentUser?.displayName || "Athlete"}
        </span>
      </p>

      {/* Show profile completion message for users who haven't completed registration */}
      {!isProfileComplete && (
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mt-4 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-blue-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                <strong>Complete Your Profile</strong>
                <br />
                Please complete your profile registration to access all
                features. You'll be redirected to the registration page.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Show approval status message for non-approved users */}
      {isProfileComplete && !isApproved && !isAdmin && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mt-4 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-yellow-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                <strong>Account Pending Approval</strong>
                <br />
                Your account is currently pending approval. You can access your
                profile and dashboard, but additional features like events,
                tickets, and resources will be available once your account is
                approved by an administrator.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="mt-4">
        <p>
          This is your dashboard. Here you'll find an overview of your
          activities and upcoming events.
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-4 mt-4">
        <p className="text-lg font-medium mb-3">Install the Wattlenest App</p>

        <div className="flex flex-col items-center gap-6">
          {installStatus !== "installed" && (
            <button
              onClick={handleInstallButtonClick}
              className="w-full py-2 px-4 border bg-goldlight border-gold text-gold hover:bg-goldlight focus:outline-none focus:ring-2 focus:ring-gold focus:ring-opacity-50 rounded-md transition-colors text-center font-medium"
            >
              Install App
            </button>
          )}
          {installStatus === "installed" && (
            <p className="text-green-600">✓ App is installed</p>
          )}
        </div>
      </div>

      {/* Install Instructions Snackbar */}
      {showInstallInstructions && (
        <div className="fixed bottom-20 left-0 right-0 mx-auto mb-20 max-w-lg px-4">
          <div className="bg-gold border-l-4 border-goldlight p-4 rounded shadow-lg">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <div className="flex justify-between items-center">
                  <p className="text-sm text-white font-medium">
                    {isIOS ? (
                      <>
                        <strong>Install on iOS:</strong>
                        <br />
                        1. Tap the Share button in Safari
                        <br />
                        2. Scroll down and select "Add to Home Screen"
                        <br />
                        3. Tap "Add" in the top right corner
                      </>
                    ) : isAndroid ? (
                      <>
                        <strong>Install on Android:</strong>
                        <br />
                        1. Tap the menu icon (three dots) in Chrome
                        <br />
                        2. Select "Add to Home screen" or "Install app"
                        <br />
                        3. Follow the on-screen instructions
                      </>
                    ) : (
                      <>
                        <strong>Install on your device:</strong>
                        <br />
                        In your browser, look for "Install this site as an app"
                        or "Add to Home Screen" in the menu options.
                      </>
                    )}
                  </p>
                  <button
                    onClick={() => setShowInstallInstructions(false)}
                    className="ml-4 text-goldlight hover:text-white"
                  >
                    <svg
                      className="h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
